import { useSearchParams } from 'react-router-dom'
import {
  billingsFiscalMonths,
  billingsFiscalYearOf,
  billingsQuarterOf,
  currentMonthKey,
  type BillingsGranularity,
} from '@/lib/billings-fiscal'
import type { BillingsFiscalYear } from '@/types'

/**
 * The period every Sales Trackers page is looking at: a fiscal year, and a window inside
 * it of a whole year, one quarter, or one month.
 *
 *
 * IT LIVES IN THE URL, AND THAT IS NOT A PREFERENCE
 *
 * `/sales-trackers/reps` and `/sales-trackers/reps/:ident` are separate routes with
 * separate elements, so choosing a rep unmounts the page and mounts a fresh copy. Held in
 * useState, the fiscal year silently snapped back to the latest year every time somebody
 * picked a rep. The period has exactly the same problem and a worse failure: a reader who
 * filtered to September, clicked a rep, and was shown the whole year would have no way of
 * telling from the screen.
 *
 * Before this hook the year was held three different ways across five pages - two
 * byte-identical copies of a useSearchParams block, and three useState fallbacks that each
 * resolved "no year chosen" differently. One place is what stops them drifting again.
 *
 *
 * THE DEFAULT IS THE CURRENT MONTH
 *
 * Not the latest month holding billings. An earlier design chose that, reasoning that
 * uploads land after a period closes so the current month is usually empty - but TARGETS
 * are already in the system, set from the Targets page and stored against a month key with
 * no dependence on a billings line. The current month therefore shows a real target with
 * whatever has landed against it so far, which is the question somebody opening this page
 * is asking.
 */
export interface BillingsPeriod {
  /** undefined until a year is known, exactly as the old `fy` state behaved. */
  fy?: number
  granularity: BillingsGranularity
  /** 'YYYY-MM', when the granularity is month. */
  month?: string
  /** 1-4, when the granularity is quarter. */
  quarter?: number
}

export function useBillingsPeriod(
  years?: BillingsFiscalYear[],
  /**
   * Query params a page owns that stop making sense when the period moves.
   *
   * The Transactions page's page number is the case this exists for: switching from
   * September to August while on page 5 kept page 5, so a shorter month rendered an empty
   * table under a summary reporting hundreds of rows. Same rule the hook already applies to
   * a month when the year changes, and that WeeklyForecast applies to its week.
   */
  alsoClear: string[] = []
) {
  const [params, setParams] = useSearchParams()

  const monthParam = params.get('month') ?? undefined
  const quarterParam = Number(params.get('quarter'))
  const granularityParam = params.get('period') as BillingsGranularity | null

  // With nothing in the URL, open on the current month — but only if the year it belongs
  // to is one the picker would offer. Before the first upload, or for a month in a year
  // nobody has touched, fall back to the newest year on offer as a whole year, which is
  // what this section did before periods existed.
  const thisMonth = currentMonthKey()
  const thisMonthFy = billingsFiscalYearOf(thisMonth)
  const offered = years?.some((y) => y.fiscal_year === thisMonthFy) ?? false
  const defaultsToThisMonth = !granularityParam && !monthParam && offered

  const fyParam = Number(params.get('fy'))
  const explicitFy = Number.isInteger(fyParam) && fyParam > 0 ? fyParam : undefined

  const granularity: BillingsGranularity = granularityParam
    ? granularityParam
    : defaultsToThisMonth
      ? 'month'
      : 'year'

  // A month implies its fiscal year, so a URL carrying only ?month= is coherent. Resolved
  // BEFORE the month, because the month has to be checked against it.
  const fy =
    explicitFy ??
    (monthParam ? billingsFiscalYearOf(monthParam) : defaultsToThisMonth ? thisMonthFy : undefined)

  /**
   * THE MONTH MUST BELONG TO THE FISCAL YEAR, and nothing upstream guarantees it.
   *
   * Sitting on FY2026 and switching to "a month" used to hand back the CURRENT month,
   * which is a month of FY2027. The API refuses a month outside the year it was asked
   * about, so the page answered a perfectly ordinary click with an error. Anything that
   * does not belong falls back to a month that does: the current one when it fits,
   * otherwise the last month of the year being looked at, which for a past year is the
   * most recent month it has.
   */
  const inYear = (key?: string) =>
    key && fy !== undefined && billingsFiscalYearOf(key) === fy ? key : undefined

  const month =
    granularity === 'month'
      ? (inYear(monthParam) ??
        inYear(thisMonth) ??
        (fy !== undefined ? billingsFiscalMonths(fy)[11]?.key : undefined))
      : undefined

  const quarter =
    granularity === 'quarter'
      ? Number.isInteger(quarterParam) && quarterParam >= 1 && quarterParam <= 4
        ? quarterParam
        : // Same rule as the month: the current quarter only when the current month is
          // actually in this year, otherwise the year's last.
          inYear(thisMonth)
          ? billingsQuarterOf(thisMonth)
          : 4
      : undefined

  /**
   * Write the period back.
   *
   * CHANGING ONE PART INVALIDATES THE OTHERS. Choosing a new fiscal year while a month of
   * the old one is selected would ask for a month that is not in the year, which the API
   * refuses outright; and a quarter number left over from a month view means nothing.
   * Same rule WeeklyForecast applies when its month changes and its week cannot survive.
   */
  const setPeriod = (next: Partial<BillingsPeriod>) => {
    const p = new URLSearchParams(params)
    const merged = { fy, granularity, month, quarter, ...next }

    if (next.fy !== undefined && next.fy !== fy) {
      // The year moved, so anything naming a month of the old one is stale.
      merged.month = undefined
      if (merged.granularity === 'month') merged.granularity = 'year'
    }

    // Never write an empty fy: it makes a shared URL read like a broken one, and the
    // hook can resolve the year perfectly well from the month or the years on offer.
    if (merged.fy !== undefined) p.set('fy', String(merged.fy))
    else p.delete('fy')
    p.set('period', merged.granularity)
    p.delete('month')
    p.delete('quarter')
    if (merged.granularity === 'month' && merged.month) p.set('month', merged.month)
    if (merged.granularity === 'quarter' && merged.quarter) {
      p.set('quarter', String(merged.quarter))
    }
    for (const key of alsoClear) p.delete(key)
    setParams(p, { replace: true })
  }

  /** The query string to carry across a navigation that remounts the page. */
  const search = params.toString()

  return { fy, granularity, month, quarter, setPeriod, search }
}

/** What the API should be asked for. Mirrors `resolvePeriod` in routes/billings.js. */
export function periodQuery(period: {
  granularity: BillingsGranularity
  month?: string
  quarter?: number
}) {
  return {
    period: period.granularity,
    month: period.granularity === 'month' ? period.month : undefined,
    quarter: period.granularity === 'quarter' ? period.quarter : undefined,
  }
}

/** How the period reads on screen, e.g. "September 2025", "Q3 2025/26", "FY2026". */
export function periodLabel(
  granularity: BillingsGranularity,
  opts: { fy?: number; month?: string; quarter?: number; monthLabel?: string }
): string {
  if (granularity === 'month') return opts.monthLabel ?? opts.month ?? 'This month'
  if (granularity === 'quarter') return `FY${opts.fy} Q${opts.quarter}`
  return `FY${opts.fy}`
}
